import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbcCommentsListComponent,
  DocumentsEditorCardComponent,
  PropertiesEditorFormComponent,
} from '@barthauer/abc/common-editors';
import { CompareHelper, ConvertHelper, ObjectHelper } from '@barthauer/abc/core';
import {
  ICoreDataEntity,
  PendingObjectsChangedPackage,
  TargetColumnValue,
  ValueDefinition,
} from '@barthauer/abc/core/data';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcCameraDetailsService, AbcGalleryComponent } from '@barthauer/abc/ui-components';
import { DxPopupComponent } from 'devextreme-angular';
import { forkJoin, Observable } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';
import { DataFormStyle, LayoutUsage } from '../../abc-arena.service';
import { ArenaService } from '../arena.service';
import { ColumnObject } from '../models/layout.model';
import { LayoutViewModel } from '../viewModels/layout.viewModel';
import { Binding } from './core/binding';
import { Creator } from './core/creator';
import { DataFormSettings } from './core/settings.model';
import { DataFormService } from './data-form.service';

export type PDC = DocumentsEditorCardComponent | AbcCommentsListComponent | PropertiesEditorFormComponent;

@Component({
  selector: 'arena-data-form',
  templateUrl: './data-form.component.html',
  styleUrls: ['./data-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DataFormComponent implements OnInit, OnDestroy {
  private static readonly field_count: number = 100;

  @ViewChild('cameraPopup') cameraPopup: DxPopupComponent;

  public settings: DataFormSettings;
  public galleryIndex: number = 0;
  public gallery: HTMLElement;
  public galleryContainer: HTMLElement;

  private isReadOnlyColumns = [];
  private loadingCounter: number = 0;
  private binding: Binding;
  private creator: Creator;

  public get showLoader(): boolean {
    return this.loadingCounter > 0 && (!this.suspendLoader || !this.suspendLoader());
  }
  public get isLoaderRelevant(): boolean {
    return this.settings.layoutViewModel.allColumns.length >= DataFormComponent.field_count;
  }

  private onNotifyChanges: (hasAnyChanges: boolean) => Promise<void> = async () => {};
  public suspendLoader: () => boolean = () => false;

  constructor(
    public service: DataFormService,
    private cdr: ChangeDetectorRef,
    private abcCameraDetailsService: AbcCameraDetailsService,
    private arenaService: ArenaService,
  ) {
    this.settings = new DataFormSettings(this.service, null, null, cdr);
    this.creator = new Creator(this.service, this.settings, this.abcCameraDetailsService, this.arenaService);
    this.binding = new Binding(this.service, this.settings);
  }

  public async ngOnInit(): Promise<void> {
    this.service.crd$.pipe(takeUntil(this.settings.destroyObservables)).subscribe(() => this.detectChanges());
  }

  private isNewObject(): boolean {
    return this.service.abcArena.context.layoutUsage === LayoutUsage.New;
  }

  public async loadLayout(
    layoutViewModel: LayoutViewModel,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
    onNotifyChanges: (hasAnyChanges: boolean) => Promise<void>,
  ): Promise<void> {
    const loading = this.beginLoading();
    try {
      this.settings = new DataFormSettings(this.service, layoutViewModel, filter, this.cdr);
      this.creator = new Creator(this.service, this.settings, this.abcCameraDetailsService, this.arenaService);
      this.binding = new Binding(this.service, this.settings);
      this.onNotifyChanges = onNotifyChanges;

      if (layoutViewModel) {
        await this.getImages(objectIds[0]);
        await this.creator.create();
        const abcGallery = <AbcGalleryComponent>this.settings.abcGalleryCptRef?.componentRef.instance;
        abcGallery?.indexChanged.subscribe((index) => {
          this.galleryIndex = index;
          if (this.galleryIndex >= 1) {
            this.calcWidthGallery();
            this.setDrawerOpened(true, false, undefined, true);
          }
        });

        this.enableAllFields(this.isNewObject());

        //TODO: Häh?
        this.arenaObservables().subscribe();

        this.creator.updateTabsUi(null);
      }

      this.detectChanges();
    } finally {
      this.endLoading(loading, true);
    }
  }

  private enableAllFields(enabled: boolean): void {
    this.settings.layoutViewModel?.columnsObjectMap.forEach((columnObject, key) => {
      columnObject.forEach((column) => {
        if (column.Control) {
          column.Control['disabled'] = !enabled;
        }
        if (column.LayoutColumn.IsReadOnly) {
          this.isReadOnlyColumns[column.Key] = column;
        }
      });
    });
  }

  public ngOnDestroy(): void {
    this.loadingCounter = 0;
    if (this.settings.layoutViewModel) {
      this.settings.layoutViewModel.images = [];
      this.settings.layoutViewModel.layoutObject.Documents = [];
    }
    this.service.documentImages = [];
  }

  private arenaObservables(): Observable<any> {
    return forkJoin([this.addObjectForDelete$(), this.pendingObjectChanged$()]);
  }

  private async getImages(id: string): Promise<void> {
    if (this.settings.layoutViewModel.hasDocumentsEditorInLayout()) {
      this.settings.layoutViewModel.layoutObject.Documents = (await this.service.getDocumentsByKind(id, 'IMAGE')) || [];
    }
  }

  public async setCurrentObject(
    object: ICoreDataEntity,
    onNotifyChanges: (hasAnyChanges: boolean) => Promise<void>,
  ): Promise<ICoreDataEntity> {
    const loading = this.isLoaderRelevant ? this.beginLoading() : null;
    try {
      if (onNotifyChanges) {
        this.onNotifyChanges = onNotifyChanges;
      }

      if (object?.Id && this.settings.layoutViewModel) {
        if (this.settings.selectedObject?.Object?.Id) {
          await this.getImages(object.Id);
        }

        this.settings.selectedObject.Object = object;
        const dataObject: ICoreDataEntity = ConvertHelper.arrayBufferToObject(
          await this.service.data
            .readDataFormObjects(this.service.arena.arenaName, this.settings.layoutViewModel, [object?.Id as string])
            .toPromise(),
        );
        if (dataObject) {
          this.enableAllFields(true);
          this.settings.selectedObject.data = dataObject;
          Object.entries(dataObject).forEach(([key, value]) => {
            if (key.startsWith('Id:')) {
              this.settings.isRefColumnsIds[key] = value;
            }
          });
          await this.binding.bind();
          this.endLoading(loading);

          for (const setObjectFunction of this.settings.setCurrentObjectFunctions) {
            setObjectFunction(object);
          }

          if (this.onNotifyChanges) {
            await this.onNotifyChanges(false);
          }

          return dataObject;
        } else {
          this.setToInitialState();
          return null;
        }
      } else {
        this.setToInitialState();
        return null;
      }
    } finally {
      this.endLoading(loading);
    }
  }

  private async setToInitialState(): Promise<void> {
    const isNewObject = this.isNewObject();
    this.enableAllFields(isNewObject);
    if (this.onNotifyChanges) {
      await this.onNotifyChanges(isNewObject);
    }

    let displayTextValues: Map<string, string>;
    let defaultValues: Map<string, unknown>;
    let evaluatedColumns: Object;

    if (isNewObject) {
      if (this.settings.selectedObject) {
        this.settings.selectedObject.data = {};
      }

      const targetColumnValues: TargetColumnValue[] = [];
      try {
        const defaultColumns: Object = ConvertHelper.arrayBufferToObject(
          await this.service.data
            .evaluateDefaultValues(
              this.settings.mainObjectTypeViewModel?.name ?? this.settings.layoutViewModel.objectType,
              null,
              [this.settings.layoutViewModel.oPath, this.settings.filter],
            )
            .toPromise(),
        );

        defaultValues = new Map<string, unknown>();
        for (const key in defaultColumns) {
          if (Object.prototype.hasOwnProperty.call(defaultColumns, key)) {
            const ec = defaultColumns[key];
            defaultValues.set(key, ec);
            targetColumnValues.push({ Name: key, Value: ec });
          }
        }
      } catch (err) {
        console.error(err);
      }

      const referencingTarget = this.service.abcArena.actions.getLayoutContext()?.target;
      const referenceColumn = referencingTarget?.column;

      if (referenceColumn || defaultValues?.size) {
        const valueDefinitions: ValueDefinition[] = [];
        this.settings.isRefColumnsIds = [];
        let refColumnObject: ColumnObject;
        displayTextValues = new Map();
        this.settings.layoutViewModel?.columnsObjectMap.forEach((element) => {
          element.forEach((column) => {
            if (column.Control && !this.isReadOnlyColumns[column.Key]) {
              column.Control['disabled'] = false;
            }
            if (column.LayoutColumn.IsReadOnly) {
              this.isReadOnlyColumns[column.Key] = column;
            }
            let isRefColumn: boolean = false;
            if (column.LayoutColumn.OPath === referenceColumn) {
              refColumnObject = column;
              isRefColumn = true;
            }
            if (referenceColumn && column.LayoutColumn.OPath?.includes('(')) {
              valueDefinitions.push({
                Name: column.Key,
                OPath: column.LayoutColumn.OPath,
              });
            } else if (
              column.LayoutColumn.OPath &&
              column.LayoutColumn.IsRef &&
              (isRefColumn || defaultValues?.get(column.LayoutColumn.OPath))
            ) {
              valueDefinitions.push({
                Name: column.ValueWrapper?.fieldName,
                OPath: 'ref:' + column.LayoutColumn?.OPath,
              });
            }
          });
        });

        if (referencingTarget?.sourceId) {
          const parentIdTcv = new TargetColumnValue();
          parentIdTcv.Name = refColumnObject?.LayoutColumn?.OPath ?? 'ParentId';
          parentIdTcv.Value = referencingTarget.sourceId;
          targetColumnValues.push(parentIdTcv);
        }

        if (referencingTarget?.sourceObjectType) {
          const parentObjectTypeTcv = new TargetColumnValue();
          parentObjectTypeTcv.Name = 'ParentObjectType';
          parentObjectTypeTcv.Value = referencingTarget?.sourceObjectType;
          targetColumnValues.push(parentObjectTypeTcv);
        }

        if (valueDefinitions?.length) {
          evaluatedColumns = ConvertHelper.arrayBufferToObject(
            await this.service.data
              .evaluateValues(
                this.settings.mainObjectTypeViewModel?.name ?? this.settings.layoutViewModel.objectType,
                valueDefinitions,
                targetColumnValues,
              )
              .toPromise(),
          );

          for (const key in evaluatedColumns) {
            if (Object.prototype.hasOwnProperty.call(evaluatedColumns, key)) {
              const ec = evaluatedColumns[key];
              displayTextValues.set(key, ec);
            }
          }
        }

        if (refColumnObject && referencingTarget?.sourceId && !displayTextValues.get(referencingTarget?.column)) {
          const referencedObjectType = await this.service.core.objectSpace
            .getObjectType(referencingTarget?.sourceObjectType)
            .toPromise();
          if (referencedObjectType) {
            const obj = ConvertHelper.arrayBufferToObject(
              await this.service.core.query
                .readObjects(
                  this.service.prepareInitializationColumnQuery(referencedObjectType, referencingTarget?.sourceId),
                )
                .toPromise(),
            );
            if (!CompareHelper.isEmptyObject(obj)) {
              displayTextValues.set(referencingTarget?.column, obj['DisplayText']);
            }
          }
        }
      }
    }
    await this.binding.clear(displayTextValues);
    if (defaultValues) {
      const columnObjects = ObjectHelper.asArray(this.settings.layoutViewModel?.columnsObjectMap?.values());
      for (const e of defaultValues) {
        let added: boolean = false;
        const fieldName: string = e[0];
        const value: unknown = e[1];
        const arr = columnObjects.find((arr) => arr.find((_) => _.ValueWrapper?.fieldName === fieldName));
        if (arr) {
          for (let c of arr) {
            added = true;
            c.ValueWrapper.value = value;
            c.ValueWrapper.initialDisplayText = displayTextValues?.get(c.ValueWrapper.fieldName);
            if (c.ValueWrapper.initialDisplayText) {
              c.ValueWrapper.initialValue = c.ValueWrapper.value;
            }
          }
        }
        if (!added) {
          this.service.persistence.addObjectValue(this.settings.objectKey, fieldName, value);
        }
      }
    }

    for (const setObjectFunction of this.settings.setCurrentObjectFunctions) {
      await setObjectFunction(this.isNewObject() ? this.service.abcArena.currentDataEntity : null);
    }
  }

  private addObjectForDelete$(): Observable<void> {
    return this.service.arena.addObjectForDelete$.pipe(takeUntil(this.settings.destroyObservables)).pipe(
      map(() => {
        this.service.persistence.addObjectForDelete(this.settings.objectKey);
      }),
    );
  }

  private pendingObjectChanged$(): Observable<void> {
    return this.service.persistence.pendingObjectsChanged$.pipe(
      concatMap(async (poc: PendingObjectsChangedPackage) => {
        if (poc.category === this.service.arena.PERSISTENCE_CATEGORY && this.loadingCounter === 0) {
          if (this.onNotifyChanges) {
            await this.onNotifyChanges(poc.hasAnyChanges);
          }
        }
      }),
    );
  }

  public capture() {
    this.cameraPopup.instance.hide();
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    const context = canvas.getContext('2d');
    const video = document.getElementById('video') as CanvasImageSource;
    context.drawImage(video, 0, 0, 640, 480);
    let imageUrl = canvas.toDataURL('image/png').replace('data:image/png;base64,', '');
    this.settings.takePictureSubject.next({ imageUrl: imageUrl, isGalery: false });
    // this.insertImage(layout, imageUrl, false);
    this.closeCamera();
  }

  public closeCamera() {
    setTimeout(() => {
      const media = document.getElementById('video') as HTMLMediaElement;
      (<MediaStream>media.srcObject).getTracks().forEach((stream) => stream.stop());
    }, 500);
  }

  public async setDrawerOpened(
    sideNavIsOpened: boolean,
    sideNavIsResized: boolean = false,
    sideNavWidth: number = 250,
    setCustom: boolean = false,
  ): Promise<void> {
    if (this.service.arena?.uiconfig?.dataFormStyle === DataFormStyle.GALLERY) {
      const galleryContainer = document.querySelector('.dx-gallery-container') as HTMLElement;
      const abcGallery = this.settings.abcGalleryCptRef?.componentRef.instance as AbcGalleryComponent;
      if (!abcGallery || abcGallery.getSelectedIndex() < 1) return;
      const selectedIndex = abcGallery.getSelectedIndex();
      let galleryOffset = this.getGalleryOffset(galleryContainer.offsetWidth, sideNavWidth, sideNavIsOpened, setCustom);

      this.updateGalleryContainerTransform(
        galleryContainer,
        galleryOffset,
        selectedIndex,
        sideNavIsOpened,
        sideNavIsResized,
        sideNavWidth,
      );
    }

    this.cdr.detectChanges();
  }

  private getGalleryOffset(
    containerWidth: number,
    sideNavWidth: number,
    sideNavIsOpened: boolean,
    setCustom: boolean,
  ): number {
    let galleryOffset = containerWidth;
    if (sideNavIsOpened) {
      galleryOffset += sideNavWidth;
      if (setCustom) {
        galleryOffset -= sideNavWidth;
      }
    }
    return galleryOffset;
  }

  private updateGalleryContainerTransform(
    galleryContainer: HTMLElement,
    offset: number,
    selectedIndex: number,
    isOpened: boolean,
    isResize: boolean,
    width: number,
  ): void {
    const adjustedOffset = isOpened ? offset * selectedIndex : (offset - (isResize ? 0 : width)) * selectedIndex;

    galleryContainer.style.transform = `translate(-${adjustedOffset}px)`;
  }

  public transformOfGallery(sidNavClicked, width) {
    if (this.galleryIndex >= 1) {
      const loading = this.beginLoading();
      try {
        this.getGallery().style.visibility = 'hidden';

        let widthGallery = this.getGalleryContainer().offsetWidth;
        if (sidNavClicked) {
          this.getGalleryContainer().style.transform =
            'translate(-' + (widthGallery + width) * this.galleryIndex + 'px)';
        } else {
          this.getGalleryContainer().style.transform =
            'translate(-' + (widthGallery - width) * this.galleryIndex + 'px)';
        }
      } finally {
        setTimeout(() => {
          this.endLoading(loading);
          this.getGallery().style.visibility = 'visible';
        }, 300);
      }
    }
  }

  private calcWidthGallery() {
    const width = this.getGalleryContainer().offsetWidth * this.galleryIndex;
    this.getGalleryContainer().style.transform =
      'translate(' + this.service?.arena?.rtlEnabled ? '+' : '-' + width + 'px)';
  }

  private getGalleryContainer(): HTMLElement {
    if (!this.galleryContainer) {
      this.galleryContainer = document.querySelector('.dx-gallery-container') as HTMLElement;
    }
    return this.galleryContainer;
  }

  getGallery(): HTMLElement {
    if (!this.gallery) {
      this.gallery = document.getElementById('gallery-id') as HTMLElement;
    }
    return this.gallery;
  }

  public async togglePDC(identifier: string): Promise<void> {
    const propName = 'is' + identifier + 'Checked';
    const checked: boolean = this.settings[propName];
    this.settings[propName] = !checked;
    const tabsElement = document.getElementById('tabs');
    const fixedGroupElement = document.getElementById('fixedGroup');

    if (this.settings.groupList.length == 3 && this.settings.isPDCChecked) {
      tabsElement.style.display = 'block';
      fixedGroupElement.style.setProperty('height', 'auto', 'important');
      fixedGroupElement.style.setProperty('max-height', '30%', 'important');
      fixedGroupElement.style.setProperty('min-height', '15%', 'important');
    } else if (this.settings.groupList.length == 3 && !this.settings.isPDCChecked) {
      tabsElement.style.display = 'none';
      fixedGroupElement.style.setProperty('max-height', '100%', 'important');
    }
    await this.setCurrentObject(this.service.abcArena.currentDataEntity, null);
    this.creator.updateTabsUi(identifier);

    const abcGallery = <AbcGalleryComponent>this.settings.abcGalleryCptRef?.componentRef.instance;
    abcGallery?.indexChanged.subscribe((index) => {
      this.galleryIndex = index;
      if (this.galleryIndex >= 1) {
        this.calcWidthGallery();
        this.setDrawerOpened(true, false, undefined, true);
      }
    });

    // const abcAccordion = <AbcAccordionComponent>this.settings.abcAccordionCptRef?.componentRef.instance;
    // if (abcAccordion) {
    //   const newItemIndex = this.settings.accordionDataSource.findIndex(
    //     (item) => item.text === identifier && item.visible
    //   );
    //   abcAccordion.selectedIndex = newItemIndex > 0 ? newItemIndex : 0;
    //   setTimeout(() => {
    //     const item = document.querySelector('.dx-item-selected');
    //     item?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    //   }, 500);
    // }
  }

  public beginLoading(): { active: boolean } {
    this.loadingCounter++;
    this.detectChanges();
    const token = { active: true };
    return token;
  }

  public endLoading(token: { active: boolean }, noChangeDetection?: boolean) {
    if (token?.active) {
      token.active = false;
      this.loadingCounter--;
    }
    if (!noChangeDetection) {
      this.detectChanges();
    }
  }

  private detectChanges(): void {
    return this.cdr.detectChanges();
  }

  public get hasChanges(): boolean {
    return this.settings.isGridOnFormOnEditing;
  }
}
